import { useEffect, useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import { ErrorNotice, Loading } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { get, post } from '../lib/api';

type Plan = {
  code: 'demo' | 'journal' | 'thesis';
  name: string;
  description: string;
  durationDays: number;
  priceId: string;
  amount: number;
  currency: string;
};

type License = {
  id: string;
  status: string;
  starts_at: string;
  expires_at: string | null;
  plan_code: string;
  name: string;
  assigned_projects: number;
};

const benefits: Record<Plan['code'], string[]> = {
  demo: ['Coba alur Project Brain', 'AI sangat terbatas', 'Tanpa ekspor dokumen lengkap'],
  journal: ['1 artikel jurnal', 'Fair-use AI terkelola', 'DOCX, dan PDF setelah renderer aktif'],
  thesis: ['1 penelitian utama', '1 artikel jurnal turunan', 'BAB I–V, revisi, dan finalisasi']
};

export function CommercialPage({ navigate }: { navigate: (path: string) => void }) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState('');
  const [paymentsAvailable, setPaymentsAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void get<{ capabilities?: { payments?: boolean } }>('/api/health')
      .then((health) => {
        setPaymentsAvailable(Boolean(health.capabilities?.payments));
      })
      .catch(() => {
        setPaymentsAvailable(false);
      });

    Promise.all([
      get<{ plans: Plan[] }>('/api/commercial/plans'),
      get<{ licenses: License[] }>('/api/account/licenses')
    ])
      .then(([catalog, owned]) => {
        setPlans(catalog.plans);
        setLicenses(owned.licenses);
      })
      .catch((caught) =>
        setError(caught instanceof Error ? caught.message : 'Paket belum termuat.')
      )
      .finally(() => setLoading(false));
  }, []);

  async function checkout(plan: Plan) {
    setError('');

    if (paymentsAvailable !== true) {
      setError('Pembayaran online belum diaktifkan.');
      return;
    }

    setPaying(plan.code);
    try {
      const result = await post<{ redirectUrl: string }>('/api/orders', {
        priceId: plan.priceId,
        idempotencyKey: `checkout-${crypto.randomUUID()}`
      });
      window.location.assign(result.redirectUrl);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Pembayaran belum dapat dimulai.');
      setPaying('');
    }
  }

  return (
    <div className="commercial-page">
      <header className="dashboard-header">
        <button className="brand-button" onClick={() => navigate('/app')}>
          <BrandMark />
        </button>
        <button className="button button-secondary" onClick={() => navigate('/app')}>
          Kembali ke proyek
        </button>
      </header>
      <main className="commercial-main">
        <div className="page-heading commercial-heading">
          <div>
            <span className="eyebrow">Paket per proyek</span>
            <h1>Pilih ruang kerja yang Anda perlukan.</h1>
            <p>
              Harga berasal dari konfigurasi server. Pembayaran divalidasi melalui webhook Midtrans,
              bukan dari status browser.
            </p>
          </div>
        </div>
        {error ? <ErrorNotice message={error} /> : null}
        {loading ? (
          <Loading label="Menyiapkan pilihan paket…" />
        ) : (
          <section className="pricing-grid" aria-label="Pilihan paket">
            {plans.map((plan) => (
              <article
                className={`pricing-plan ${plan.code === 'thesis' ? 'featured' : ''}`}
                key={plan.code}
              >
                <SenoIcon name={plan.code === 'demo' ? 'license' : 'payment'} />
                <span>{plan.durationDays ? `${plan.durationDays} hari` : 'Try before buy'}</span>
                <h2>{plan.name}</h2>
                <strong className="plan-price">
                  {new Intl.NumberFormat('id-ID', {
                    style: 'currency',
                    currency: plan.currency,
                    maximumFractionDigits: 0
                  }).format(plan.amount)}
                </strong>
                <p>{plan.description}</p>
                <ul>
                  {benefits[plan.code].map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                  ))}
                </ul>
                {plan.code === 'demo' ? (
                  <button
                    className="button button-secondary button-wide"
                    onClick={() => navigate('/app')}
                  >
                    Demo tersedia otomatis
                  </button>
                ) : (
                  <button
                    className="button button-primary button-wide"
                    disabled={Boolean(paying) || paymentsAvailable !== true}
                    title={
                      paymentsAvailable === false
                        ? 'Pembayaran online belum diaktifkan.'
                        : undefined
                    }
                    onClick={() => checkout(plan)}
                  >
                    {paymentsAvailable !== true
                      ? 'Pembayaran belum tersedia'
                      : paying === plan.code
                        ? 'Membuka pembayaran…'
                        : `Pilih ${plan.name}`}
                  </button>
                )}
              </article>
            ))}
          </section>
        )}
        <section className="owned-licenses">
          <div className="section-title">
            <div>
              <span className="eyebrow">Lisensi Anda</span>
              <h2>Ruang proyek yang tersedia</h2>
            </div>
          </div>
          {licenses.length ? (
            <div className="license-list">
              {licenses.map((license) => (
                <article key={license.id}>
                  <SenoIcon name="license" />
                  <div>
                    <strong>{license.name}</strong>
                    <span>
                      {license.status} · {license.assigned_projects} proyek terhubung
                      {license.expires_at
                        ? ` · sampai ${new Date(license.expires_at).toLocaleDateString('id-ID')}`
                        : ''}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p>Lisensi Demo dibuat ketika proyek pertama dimulai.</p>
          )}
        </section>
        <p className="payment-note">
          {paymentsAvailable === true
            ? 'QRIS dan metode pembayaran lain ditampilkan pada halaman pembayaran aman Midtrans. Tidak ada saldo kredit atau top-up AI di SenoSkripsi.'
            : 'Pembayaran online belum diaktifkan. Paket berbayar belum dapat dibeli saat ini. Tidak ada saldo kredit atau top-up AI di SenoSkripsi.'}
        </p>
      </main>
    </div>
  );
}
